"use client";

import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

type ConsentState = {
  granted: boolean;
  hasChoice: boolean;
  loading: boolean;
  grant: () => void;
  deny: () => void;
  set: (value: boolean) => void;
};

const CONSENT_COOKIE = "hasAcceptedCookies"; // align with website cookie name
const CONSENT_LOCALSTORAGE = "hasAcceptedCookies";

type ConsentRead = {
  value: boolean;
  hasChoice: boolean;
};

function parseConsentValue(value: string) {
  return value === "true" || value === "1" || value === "granted";
}

export function readConsent(): ConsentRead {
  try {
    // Prefer explicit cookie
    const cookie = Cookies.get(CONSENT_COOKIE);
    if (typeof cookie !== "undefined") {
      return { value: parseConsentValue(cookie), hasChoice: true };
    }
    // Fallback to localStorage (e.g., set by other apps)
    if (typeof window !== "undefined") {
      const ls = window.localStorage.getItem(CONSENT_LOCALSTORAGE);
      if (ls !== null) {
        return { value: parseConsentValue(ls), hasChoice: true };
      }
    }
  } catch (_) {}
  return { value: false, hasChoice: false };
}

export function useMarketingConsent(): ConsentState {
  const [granted, setGranted] = useState<boolean>(false);
  const [hasChoice, setHasChoice] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const { value, hasChoice } = readConsent();
    setGranted(value);
    setHasChoice(hasChoice);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleUpdate = () => {
      const { value, hasChoice } = readConsent();
      setGranted(value);
      setHasChoice(hasChoice);
    };

    window.addEventListener("marketing-consent-change", handleUpdate);
    window.addEventListener("storage", handleUpdate);

    return () => {
      window.removeEventListener("marketing-consent-change", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
    };
  }, []);

  const persist = useCallback((value: boolean) => {
    try {
      Cookies.set(CONSENT_COOKIE, value ? "true" : "false", {
        expires: 365,
        sameSite: "Lax",
        path: "/",
      });
      if (typeof window !== "undefined") {
        window.localStorage.setItem(CONSENT_LOCALSTORAGE, value ? "true" : "false");
      }
    } catch (_) {}
  }, []);

  const set = useCallback(
    (value: boolean) => {
      setGranted(value);
      setHasChoice(true);
      persist(value);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("marketing-consent-change"));
      }
    },
    [persist],
  );

  const grant = useCallback(() => set(true), [set]);
  const deny = useCallback(() => set(false), [set]);

  return { granted, hasChoice, loading, grant, deny, set };
}
